// Quotes.jsx
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import QuoteDialog from '../../components/QuoteDialog';
import { formatDate } from '../../utils/formatDate';
import { formatDateTime } from '../../utils/formatDateTime';

export default function Quotes() {
  const {
    quotes, setQuotes, quotesLoading, fetchQuotes, // from global
  } = useOutletContext();

  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');


  // Client-side filtering to avoid API churn on page swaps
  const filtered = useMemo(() => {
  return quotes.filter((q) => {
    const okStatus = statusFilter ? q.status === statusFilter : true;
    const okCat = categoryFilter ? q.category === categoryFilter : true;
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      !term ||
      q.firstName?.toLowerCase().includes(term) ||
      q.lastName?.toLowerCase().includes(term) ||
      q.category?.toLowerCase().includes(term) ||
      q.status?.toLowerCase().includes(term) ||
      q.date?.toLowerCase?.()?.includes(term) ||
      q.servings?.toString().includes(term);

    return okStatus && okCat && matchesSearch;
  });
}, [quotes, statusFilter, categoryFilter, searchTerm]);

  const isSentToday = (sentAt) => {
    if (!sentAt) return false;
    const sent = new Date(sentAt);
    const today = new Date();
    return (
      sent.getFullYear() === today.getFullYear() &&
      sent.getMonth() === today.getMonth() &&
      sent.getDate() === today.getDate()
    );
  };

  const rowCls = 'cursor-pointer transition even:bg-gray-50 hover:bg-candy-background/30';

  return (
    <div className="p-4 sm:p-6 bg-candy-background rounded-2xl">
      <div className="mb-4 flex flex-wrap gap-3 items-center">
  <h1 className="text-xl font-bold">Quote Requests</h1>

  {/* 🔍 Search Bar */}
  <input
    type="text"
    placeholder="Search by name, category, date, or status..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className="flex-1 min-w-[200px] px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-candy-primary"
  />

  <div className="ml-auto flex gap-2">
    <select
      value={categoryFilter}
      onChange={e => setCategoryFilter(e.target.value)}
      className="px-3 py-2 border rounded"
    >
      <option value="">All Categories</option>
      <option value="wedding">Wedding</option>
      <option value="custom">Custom</option>
    </select>

    <select
      value={statusFilter}
      onChange={e => setStatusFilter(e.target.value)}
      className="px-3 py-2 border rounded"
    >
      <option value="">All Statuses</option>
      <option value="new">New</option>
      <option value="reviewed">Reviewed</option>
      <option value="quoted">Quoted</option>
      <option value="closed">Closed</option>
    </select>

    <button
      onClick={() => fetchQuotes(true)}
      className="px-3 py-2 bg-candy-primary text-white rounded"
      disabled={quotesLoading}
    >
      {quotesLoading ? 'Refreshing…' : 'Refresh'}
    </button>
  </div>
</div>


      <div className="bg-white rounded-2xl border border-candy-border overflow-x-auto">
        <table className="min-w-full text-sm table-fixed">
          <thead className="bg-candy-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left w-[18%]">Name</th>
              <th className="px-4 py-3 text-left w-[9%]">Category</th>
              <th className="px-4 py-3 text-left w-[11%]">Event Date</th>
              <th className="px-2 py-3 text-left w-[7rem] max-w-[7rem]">Servings</th>
              <th className="px-4 py-3 text-left w-[9%]">Status</th>
              <th className="px-4 py-3 text-left w-[18%] whitespace-nowrap">Customer Viewed</th>
              <th className="px-4 py-3 text-left w-[11%]">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {quotesLoading && (
              <tr><td className="px-4 py-3" colSpan={7}>Loading…</td></tr>
            )}
            {!quotesLoading && filtered.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={7}>No quotes found</td></tr>
            )}
            {!quotesLoading && filtered.map(q => (
              <tr key={q._id} className={rowCls} onClick={() => setSelected(q)}>
                <td className="px-4 py-3 whitespace-nowrap">{q.firstName} {q.lastName}</td>
                <td className="px-4 py-3 capitalize">{q.category}</td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(q.date)}</td>
                <td className="px-2 py-3 max-w-[7rem] truncate text-left text-gray-700" title={q.servings || ''}>
                  {q.servings}
                </td>
                <td className="px-4 py-3 capitalize">{q.status}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {q.latestViewedAt ? (
                    <span className="text-green-700">{formatDateTime(q.latestViewedAt)}</span>
                  ) : ['quoted', 'closed'].includes(q.status) && isSentToday(q.latestQuoteSentAt) ? (
                    <span className="text-gray-400 italic">Not yet</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDate(q.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <QuoteDialog
          quote={selected}
          onClose={() => setSelected(null)}
          onChanged={(updated) => {
            setQuotes(prev => prev.map(q => q._id === updated._id ? updated : q));
            setSelected(updated);
          }}
          onDeleted={(id) => {
            setQuotes(prev => prev.filter(q => q._id !== id));
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}
